import React from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';

import forwardRefWithTranslation from '@/common/forwardRefWithTranslation';
import { getCountryAndNationalPhoneNumberFromRawPhoneNumber } from '@/common/helpers/phone_number';
import {
    setupFacebook,
    setupApple,
    setShowLoadingSpinner,
    setHideLoadingSpinner,
    FacebookButton,
} from '@/utils/social-login-callbacks';

import IncompleteForm from './incomplete-form';
import SignInForm from './sign-in-form';
import SignUpForm from './sign-up-form';
import SignedInForm from './signed-in-form';

const {
    Customer,
    Order,
    UI,
    helpers: { sanitiser, validator },
    headoffice: {
        customer_phone_number_validation_enabled,
        facebook_sign_in_enabled,
        apple_sign_in_enabled,
    },
} = Mobi2Go;

const EMAIL_INPUT_DEBOUNCE_WAIT = 500;
const LOYALTY_ID_INPUT_DEBOUNCE_WAIT = 500;

class CustomerSection extends React.Component {
    static propTypes = {
        t: PropTypes.func.isRequired,
    };

    state = {
        email_input_value: '',
        login_password_input_value: '',
        register_password_input_value: '',
        name_input_value: '',
        phone_number_input_value: '',
        phone_number_country_input_value:
            Mobi2Go.headoffice.country_iso3166_alpha2,
        loyalty_id_input_value: '',
        credit_balance_value: null,
        terms_and_conditions_input_value: false,
        subscriber_input_value:
            Mobi2Go.headoffice.customer_subscriber_default_state,
        section_status: '',
        section_icon: '',
        form_type: 'incomplete',
        is_forgot_password_visible: true,
        is_guest: false,
    };

    login_failures = {};

    componentDidMount() {
        Customer.bind({
            logIn: this.onCustomerLogIn,
            logInFail: this.onCustomerLogInFail,
            logOut: this.onCustomerLogOut,
            updateCreditBalance: this.onCustomerUpdateCreditBalance,
            update: this.onCustomerUpdate,
        });
        UI.bind(
            'customerPasswordRecoveryCancelled',
            this.onCustomerPasswordRecoveryCancelled
        );

        /**
         * IE patch
         * Session state gets executed before main app payload
         */
        if (Customer.isLoggedIn()) {
            this.setState({
                form_type: 'signedin',
                email_input_value: Customer.getEmail(),
                name_input_value: Customer.getName(),
                phone_number_input_value: Customer.getPhoneNumber(),
                loyalty_id_input_value: Customer.getLoyaltyId(),
                is_guest: Customer.isGuest(),
            });
        }

        // Remember email cookie
        Mobi2Go.app.onStateReady(state => {
            const saved_email = Customer.getSavedEmail();
            if (saved_email && (!state.customer || !state.customer.email)) {
                this.setState(
                    {
                        email_input_value: saved_email,
                    },
                    this.onValidateEmail
                );
            }
        });

        facebook_sign_in_enabled && setupFacebook();
        apple_sign_in_enabled && setupApple();

        if (facebook_sign_in_enabled || apple_sign_in_enabled) {
            setShowLoadingSpinner(() => {
                this.setSectionState('', 'spinner');
            });
            setHideLoadingSpinner(() => {
                this.setSectionState('', '');
            });
        }
    }

    componentWillUnmount() {
        Customer.unbind({
            logIn: this.onCustomerLogIn,
            logInFail: this.onCustomerLogInFail,
            logOut: this.onCustomerLogOut,
            updateCreditBalance: this.onCustomerUpdateCreditBalance,
            update: this.onCustomerUpdate,
        });
        UI.unbind(
            'customerPasswordRecoveryCancelled',
            this.onCustomerPasswordRecoveryCancelled
        );
    }

    onCustomerPasswordRecoveryCancelled = () => {
        this.setState({
            is_forgot_password_visible: true,
        });
    };

    onValidateEmail = () => {
        const { email_input_value } = this.state;
        const { t } = this.props;
        if (validator.email(email_input_value).is_valid) {
            this.setSectionState('', 'spinner');
            Customer.isEmailRegistered(email_input_value, is_registered => {
                if (Customer.isLoggedIn()) {
                    return;
                }

                if (is_registered) {
                    this.showSignInForm();
                } else {
                    this.showSignUpForm();
                }
            });
        } else if (email_input_value === '') {
            this.resetForm();
            // Clear email cookie when email field is completely deleted
            Customer.clearSavedEmail();
        } else {
            this.setSectionState(t('enter_email'), 'mail');
        }
    };

    onValidateEmailDebounced = _.debounce(
        this.onValidateEmail,
        EMAIL_INPUT_DEBOUNCE_WAIT
    );

    onCustomerLogIn = customer => {
        let { phone_number } = customer;
        let phone_number_country = Mobi2Go.headoffice.country_iso3166_alpha2;

        if (customer_phone_number_validation_enabled) {
            try {
                [
                    phone_number,
                    phone_number_country,
                ] = getCountryAndNationalPhoneNumberFromRawPhoneNumber(
                    customer.phone_number
                );
            } catch (e) {
                // Use default country and raw phone number
            }
        }

        this.setState({
            form_type: 'signedin',
            section_status: '',
            section_icon: '',
            email_input_value: customer.email,
            name_input_value: customer.name,
            phone_number_input_value: phone_number,
            phone_number_country_input_value: phone_number_country,
            loyalty_id_input_value: customer.loyalty_id,
            is_guest: customer.is_guest,
        });

        this.login_failures = {};

        UI.closeAlert(); // 'Forgot password' alert may be open
    };

    onCustomerLogInFail = () => {
        const { t } = this.props;
        const { login_password_input_value } = this.state;
        if (this.login_failures[login_password_input_value] === undefined) {
            this.login_failures[login_password_input_value] = 0;
        }

        const current_password_failure_count = this.login_failures[
            login_password_input_value
        ];
        this.login_failures[login_password_input_value] += 1;
        const unique_password_failure_count = Object.keys(this.login_failures)
            .length;

        if (
            current_password_failure_count >= 4 ||
            unique_password_failure_count >= 3
        ) {
            this.promptPasswordReset();
        } else {
            UI.alert({
                id: 'password-incorrect',
                title: t('login_failed_password_title'),
                body: t('login_failed_password_body'),
                el:
                    this.sign_in_form_ref &&
                    this.sign_in_form_ref.getPasswordInputContainerRef(),
                primary_button: null,
            });
        }

        this.setSectionState(t('incorrect_password'), 'alert');
    };

    onCustomerLogOut = () => {
        this.resetForm();
    };

    onCustomerUpdateCreditBalance = credit_balance => {
        this.setState({
            credit_balance_value: credit_balance,
        });
    };

    onCustomerUpdate = () => {
        let phone_number = Customer.getPhoneNumber();
        let phone_number_country = Mobi2Go.headoffice.country_iso3166_alpha2;

        if (customer_phone_number_validation_enabled) {
            try {
                [
                    phone_number,
                    phone_number_country,
                ] = getCountryAndNationalPhoneNumberFromRawPhoneNumber(
                    Customer.getPhoneNumber()
                );
            } catch (e) {
                // Use default country and raw phone number
            }
        }

        this.setState({
            email_input_value: Customer.getEmail(),
            name_input_value: Customer.getName(),
            phone_number_input_value: phone_number,
            phone_number_country_input_value: phone_number_country,
            loyalty_id_input_value: Customer.getLoyaltyId(),
            is_guest: Customer.isGuest(),
        });
    };

    onEmailInputChange = value => {
        // CB 4/2/21 - Cancel debounce if customer starts typing again
        this.onValidateEmailDebounced.cancel();
        this.setState(
            {
                email_input_value: sanitiser.email(value),
            },
            this.onValidateEmailDebounced
        );
    };

    onLoginPasswordInputChange = value =>
        this.setState({ login_password_input_value: value });

    onRegisterPasswordInputChange = value =>
        this.setState({ register_password_input_value: value });

    onTermsAndConditionsInputChange = value =>
        this.setState({ terms_and_conditions_input_value: value });

    onSubscriberInputChange = value =>
        this.setState({ subscriber_input_value: value });

    onNameInputChange = value => {
        const { form_type } = this.state;
        if (form_type !== 'incomplete' && form_type !== 'signup') {
            return;
        }
        this.setState({
            name_input_value: value,
        });
    };

    onPhoneNumberInputChange = value => {
        const { form_type } = this.state;
        if (form_type !== 'incomplete' && form_type !== 'signup') {
            return;
        }
        this.setState({
            phone_number_input_value: customer_phone_number_validation_enabled
                ? value
                : sanitiser.phoneNumber(value),
        });
    };

    onPhoneNumberCountryChange = value =>
        this.setState({ phone_number_country_input_value: value });

    onLoyaltyIdUpdated = () => {
        const { loyalty_id_input_value } = this.state;
        Customer.update(
            {
                loyalty_id: loyalty_id_input_value,
            },
            () =>
                this.setState({
                    loyalty_id_input_value: Customer.getLoyaltyId(),
                })
        );
    };

    onLoyaltyIdUpdateDebounced = _.debounce(
        this.onLoyaltyIdUpdated,
        LOYALTY_ID_INPUT_DEBOUNCE_WAIT
    );

    showSignUpForm = () => {
        const { t } = this.props;
        this.setState(
            {
                form_type: 'signup',
                section_status: t('new_account'),
                section_icon: 'down',
            },
            () => {
                if (this.sign_up_form_ref) {
                    this.sign_up_form_ref.focusOnNameInput();
                }
            }
        );
        Order.log('customer sign up started');
    };

    showSignInForm = () => {
        const { t } = this.props;
        this.setState(
            {
                form_type: 'signin',
                section_status: t('existing_account'),
                section_icon: 'down',
            },
            () => {
                if (this.sign_in_form_ref) {
                    this.sign_in_form_ref.focusOnPasswordInput();
                }
            }
        );
        Order.log('customer log in started');
    };

    shouldNotBeRendered = () => {
        const container_app = Mobi2Go.app.getContainerApp();

        return (
            container_app === 'loke' ||
            container_app === 'iseeinternational' ||
            container_app === 'steer73'
        );
    };

    resetForm = () => {
        this.setState(
            {
                form_type: 'incomplete',
                email_input_value: '',
                login_password_input_value: '',
                register_password_input_value: '',
                name_input_value: '',
                phone_number_input_value: '',
                phone_number_country_input_value:
                    Mobi2Go.headoffice.country_iso3166_alpha2,
                loyalty_id_input_value: '',
                terms_and_conditions_input_value: false,
                subscriber_input_value:
                    Mobi2Go.headoffice.customer_subscriber_default_state,
                section_status: '',
                section_icon: '',
            },
            () => {
                if (this.incomplete_form_ref) {
                    this.incomplete_form_ref.focusOnEmailInput();
                }
            }
        );
    };

    promptPasswordReset = () => {
        const { email_input_value } = this.state;
        const { t } = this.props;

        UI.alert({
            title: t('forgot_password_title'),
            body: t('forgot_password_body'),
            primary_button: t('forgot_password_primary_button'),
            onPrimaryButtonClick: () => {
                Customer.sendPasswordReset(email_input_value);
                this.setState({
                    is_forgot_password_visible: false,
                });
                UI.trigger('showCustomerPasswordRecovery', email_input_value);
            },
            el:
                this.sign_in_form_ref &&
                this.sign_in_form_ref.getPasswordInputContainerRef(),
            secondary_button: t('forgot_password_secondary_button'),
            onSecondaryButtonClick: () => {
                this.login_failures = {};
            },
        });
    };

    setSectionState = (status, icon) =>
        this.setState({ section_status: status, section_icon: icon });

    accountCreatedAlert = () => {
        const { t } = this.props;
        UI.alert({
            id: 'account-created',
            title: t('customer:account_created_title'),
            body: t('customer:account_created_body'),
            temp: true,
            primary_button: null,
            el: this.root_element,
        });
    };

    onLoyaltyIdInputChange = value => {
        this.setState(
            {
                loyalty_id_input_value: value,
            },
            this.onLoyaltyIdUpdateDebounced
        );
    };

    onLoginLabelClick = () => {
        const { form_type } = this.state;
        const { t } = this.props;
        if (form_type === 'incomplete') {
            UI.alert({
                title: t('login_enter_email_title'),
                body: t('login_enter_email_body'),
                temp: true,
                primary_button: null,
                el:
                    this.incomplete_form_ref &&
                    this.incomplete_form_ref.getEmailInputContainerRef(),
            });
            if (this.incomplete_form_ref) {
                this.incomplete_form_ref.focusOnEmailInput();
            }
        }
        if (form_type === 'signin' && this.sign_in_form_ref) {
            this.sign_in_form_ref.signIn();
        }
    };

    onSignUpLabelClick = () => {
        const { form_type } = this.state;
        const { t } = this.props;
        if (form_type === 'incomplete') {
            UI.alert({
                title: t('customer:signup_enter_email_title'),
                body: t('customer:signup_enter_email_body'),
                temp: true,
                primary_button: null,
                el:
                    this.incomplete_form_ref &&
                    this.incomplete_form_ref.getEmailInputContainerRef(),
            });
            if (this.incomplete_form_ref) {
                this.incomplete_form_ref.focusOnEmailInput();
            }
        }
        if (form_type === 'signup' && this.sign_up_form_ref) {
            this.sign_up_form_ref.signUp();
        }
    };

    getCustomerClassnameStatus = () => {
        const { form_type } = this.state;
        switch (form_type) {
            case 'signin':
                return 'm2g-customer--logged-out m2g-customer--logging-in';
            case 'signup':
                return 'm2g-customer--logged-out m2g-customer--registering';
            case 'signedin':
                return 'm2g-customer--logged-in';
            default:
            case 'incomplete':
                return 'm2g-customer--logged-out';
        }
    };

    // warnForIncorrectLoginMethod = method => {
    //     this.setSectionState('', '');

    //     const { t } = this.props;
    //     UI.alert({
    //         id: 'incorrect-login-method',
    //         title: t('customer:login_failed_incorrect_method_title'),
    //         body: t('customer:login_failed_incorrect_method_body', { method }),
    //         temp: true,
    //         primary_button: null,
    //         el: this.root_element,
    //     });
    // };

    render() {
        if (this.shouldNotBeRendered()) {
            return null;
        }

        const { t } = this.props;
        const {
            email_input_value,
            login_password_input_value,
            register_password_input_value,
            name_input_value,
            phone_number_input_value,
            phone_number_country_input_value,
            credit_balance_value,
            loyalty_id_input_value,
            terms_and_conditions_input_value,
            subscriber_input_value,
            section_status,
            section_icon,
            form_type,
            is_forgot_password_visible,
            is_guest,
        } = this.state;

        return (
            <div
                data-order-section="customer"
                className={`m2g-customer m2g-order-section ${this.getCustomerClassnameStatus()}`}
                ref={ref => {
                    this.root_element = ref;
                }}
            >
                <div className="m2g-order-section-header">
                    <span className="m2g-order-section-icon m2g-icon m2g-icon--user m2g-hidden" />
                    {form_type === 'signedin' && (
                        <span className="m2g-order-section-title m2g-customer-logged-in-title">
                            {is_guest ? t('guest_account') : t('account')}
                        </span>
                    )}

                    {Mobi2Go.headoffice.customer_guest_checkout_enabled
                        ? [
                              form_type === 'signin' && (
                                  <span
                                      key="login-label"
                                      data-login-label
                                      className="m2g-order-section-title m2g-customer-login-title"
                                      onClick={this.onLoginLabelClick}
                                      onKeyPress={() => {}}
                                      role="button"
                                      tabIndex={-1}
                                  >
                                      {t('login')}
                                  </span>
                              ),
                              (form_type === 'incomplete' ||
                                  form_type === 'signup') && (
                                  <span
                                      key="guest-signup-label"
                                      data-guest-signup-label
                                      className="m2g-order-section-title m2g-customer-guest-signup-title"
                                      onClick={this.onSignUpLabelClick}
                                      onKeyPress={() => {}}
                                      role="button"
                                      tabIndex={-1}
                                  >
                                      {t('guest_signup')}
                                  </span>
                              ),
                          ]
                        : [
                              (form_type === 'incomplete' ||
                                  form_type === 'signin') && (
                                  <span
                                      key="login-label"
                                      data-login-label
                                      className="m2g-order-section-title m2g-customer-login-title"
                                      onClick={this.onLoginLabelClick}
                                      onKeyPress={() => {}}
                                      role="button"
                                      tabIndex={-1}
                                  >
                                      {t('login')}
                                  </span>
                              ),
                              form_type === 'incomplete' && (
                                  <span key="separator">/</span>
                              ),
                              (form_type === 'incomplete' ||
                                  form_type === 'signup') && (
                                  <span
                                      key="signup-label"
                                      data-signup-label
                                      className="m2g-order-section-title m2g-customer-register-title"
                                      onClick={this.onSignUpLabelClick}
                                      onKeyPress={() => {}}
                                      role="button"
                                      tabIndex={-1}
                                  >
                                      {t('signup')}
                                  </span>
                              ),
                          ]}

                    <span
                        data-order-section-status-icon
                        className={`m2g-order-section-status-icon m2g-icon ${
                            section_icon ? `m2g-icon--${section_icon}` : ''
                        }`}
                    />
                    <span
                        data-order-section-status
                        className="m2g-order-section-status"
                    >
                        {section_status}
                    </span>
                </div>

                {form_type === 'incomplete' && (
                    <IncompleteForm
                        email={email_input_value}
                        onEmailChange={this.onEmailInputChange}
                        name={name_input_value}
                        onNameChange={this.onNameInputChange}
                        phone_number={phone_number_input_value}
                        onPhoneNumberChange={this.onPhoneNumberInputChange}
                        phone_number_country={phone_number_country_input_value}
                        onPhoneNumberCountryChange={
                            this.onPhoneNumberCountryChange
                        }
                        ref={ref => {
                            this.incomplete_form_ref = ref;
                        }}
                    />
                )}

                {form_type === 'signin' && (
                    <SignInForm
                        email={email_input_value}
                        onEmailChange={this.onEmailInputChange}
                        password={login_password_input_value}
                        onPasswordChange={this.onLoginPasswordInputChange}
                        is_forgot_password_visible={is_forgot_password_visible}
                        onForgotPasswordClick={this.promptPasswordReset}
                        onCreateAccountClick={this.resetForm}
                        setSectionState={this.setSectionState}
                        ref={ref => {
                            this.sign_in_form_ref = ref;
                        }}
                    />
                )}

                {form_type === 'signup' && (
                    <SignUpForm
                        email={email_input_value}
                        onEmailChange={this.onEmailInputChange}
                        name={name_input_value}
                        onNameChange={this.onNameInputChange}
                        phone_number={phone_number_input_value}
                        onPhoneNumberChange={this.onPhoneNumberInputChange}
                        phone_number_country={phone_number_country_input_value}
                        onPhoneNumberCountryChange={
                            this.onPhoneNumberCountryChange
                        }
                        password={register_password_input_value}
                        onPasswordChange={this.onRegisterPasswordInputChange}
                        terms_and_conditions={terms_and_conditions_input_value}
                        onTermsAndConditionsChange={
                            this.onTermsAndConditionsInputChange
                        }
                        subscriber={subscriber_input_value}
                        onSubscriberChange={this.onSubscriberInputChange}
                        setSectionState={this.setSectionState}
                        accountCreatedAlert={this.accountCreatedAlert}
                        ref={ref => {
                            this.sign_up_form_ref = ref;
                        }}
                    />
                )}

                {form_type === 'signedin' && (
                    <SignedInForm
                        name={name_input_value}
                        phone_number={phone_number_input_value}
                        phone_number_country={phone_number_country_input_value}
                        loyalty_id={loyalty_id_input_value}
                        onLoyaltyIdChange={this.onLoyaltyIdInputChange}
                        credit_balance={credit_balance_value}
                    />
                )}
                <div data-view="shift8-points" />
                {form_type !== 'signedin' && facebook_sign_in_enabled && (
                    <FacebookButton />
                )}
                {form_type !== 'signedin' && apple_sign_in_enabled && (
                    <div
                        id="appleid-signin"
                        data-type="continue"
                        data-height="44"
                        data-logo-position="0"
                        data-border-radius="0"
                    ></div>
                )}
            </div>
        );
    }
}

export default forwardRefWithTranslation('customer')(CustomerSection);
